import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { AuthLayout } from "./AuthLayout";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InlineSpinner } from "@/components/common/Loading";
import { Eye, EyeOff, AlertCircle } from "lucide-react";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";

export default function Login() {
  const { login, verifyMfa } = useAuth();
  const navigate = useNavigate();
  // Safe, in-app return destination (e.g. coming back to a Scheduling Page after Sign In). Never external.
  const _rawReturn = new URLSearchParams(window.location.search).get("return") || "";
  const returnTo = _rawReturn.startsWith("/") && !_rawReturn.startsWith("//") ? _rawReturn : "/";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [mfaToken, setMfaToken] = useState(null);
  const [code, setCode] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const res = await login(email.trim().toLowerCase(), password);
      if (res.mfaToken) { setMfaToken(res.mfaToken); }
      else if (res.mfaSetupRequired) navigate("/security?setup=1");
      else navigate(returnTo);
    } catch (err) { setError(apiError(err, "Invalid email or password")); }
    finally { setLoading(false); }
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await verifyMfa(mfaToken, code);
      navigate(returnTo);
    } catch (err) { setError(apiError(err)); }
    finally { setLoading(false); }
  };

  if (mfaToken) {
    return (
      <AuthLayout title="Two-factor authentication" subtitle="Enter the 6-digit code from your authenticator app.">
        <form onSubmit={submitMfa} className="space-y-5">
          {error && <ErrorBox msg={error} />}
          <div className="flex justify-center">
            <InputOTP maxLength={6} value={code} onChange={setCode} data-testid="mfa-code-input">
              <InputOTPGroup>
                {[0,1,2,3,4,5].map((i) => <InputOTPSlot key={i} index={i} />)}
              </InputOTPGroup>
            </InputOTP>
          </div>
          <Button type="submit" disabled={loading || code.length < 6} data-testid="mfa-verify-button"
            className="w-full bg-[#006090] hover:bg-[#004E75]">
            {loading ? <InlineSpinner /> : "Verify & sign in"}
          </Button>
        </form>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Sign in" subtitle="Authorized personnel and candidates only."
      tagline="A secure sign-on for our recruiting team and candidates.">
      <form onSubmit={submit} className="space-y-4">
        {error && <ErrorBox msg={error} />}
        <div>
          <Label htmlFor="email" className="text-xs font-semibold text-slate-700">Email</Label>
          <Input id="email" type="email" value={email} required data-testid="login-email"
            onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" className="mt-1.5" />
        </div>
        <div>
          <div className="flex items-center justify-between">
            <Label htmlFor="password" className="text-xs font-semibold text-slate-700">Password</Label>
            <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs text-[#0093D6] font-medium hover:underline">Forgot?</Link>
          </div>
          <div className="relative mt-1.5">
            <Input id="password" type={show ? "text" : "password"} value={password} required data-testid="login-password"
              onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
            <button type="button" onClick={() => setShow((s) => !s)} data-testid="toggle-password"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
              {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
        <label className="flex items-center gap-2 text-xs text-slate-600 select-none">
          <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} data-testid="login-remember" className="w-3.5 h-3.5" />
          Remember me on this device
        </label>
        <Button type="submit" disabled={loading} data-testid="login-submit-button" className="w-full bg-[#006090] hover:bg-[#004E75]">
          {loading ? <InlineSpinner /> : "Sign in"}
        </Button>
        <p className="text-center text-sm text-slate-600">
          New here?{" "}
          <Link to="/register" data-testid="login-create-account" className="text-[#0093D6] font-semibold hover:underline">Create an account</Link>
        </p>
        <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2.5 text-[11px] leading-relaxed text-emerald-800" data-testid="login-no-spam">
          <span className="font-semibold">We never send spam — ever.</span> Create an account only if you're an
          authorized team member or a candidate we're working with. We'll only email you about your account and
          your recruiting conversation, and you can opt out of non-essential messages at any time.
        </div>
        <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2.5 text-[11px] leading-relaxed text-slate-500" data-testid="login-privacy">
          <span className="font-semibold text-slate-600">Your information matters.</span> We use the information you provide
          to evaluate and respond to your recruiting inquiry, manage your account, and communicate with you about your
          candidacy. We do not sell your data or send marketing blasts. Communication preferences and opt-out requests are honored.
        </div>
        <p className="text-center text-[10px] text-slate-400">
          Independent recruiting portal for Cynthia L. Ryder's district organization — not an official Farmers Insurance corporate website.
        </p>
      </form>
    </AuthLayout>
  );
}

function ErrorBox({ msg }) {
  return (
    <div data-testid="auth-error" className="flex items-start gap-2 rounded-md bg-rose-50 border border-rose-200 px-3 py-2 text-sm text-rose-700">
      <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /><span>{msg}</span>
    </div>
  );
}
